import { Column, Index, PrimaryGeneratedColumn } from 'typeorm';
import { Entity } from '../../decorators';

/** Lifecycle of one pipeline stage row. */
export type LicensePipelineStageStatus =
	| 'running'
	| 'completed'
	| 'failed'
	| 'skipped';

/**
 * Ordered stages of the nightly per-tenant licensing pipeline (PD-14439).
 * `match` → `prune` → `auto_confirm` → `stats_refresh`; a stage that is not
 * enabled for a run is still recorded as `skipped` so the log always shows
 * the full picture.
 */
export type LicensePipelineStage =
	| 'match'
	| 'prune'
	| 'auto_confirm'
	// Copies the verified lm_registry values onto confirmed-matched
	// client_license rows. It is a stage rather than its own schedule because
	// the matchmaker CANNOT do this work — it skips every client whose licences
	// are all linked — and because a separate cron silently stopped for five
	// days without anyone noticing (PD-15247).
	| 'registry_reapply'
	// Link drift (2026-09-05): act on regulator links that have ENDED since a
	// confirm — flag human confirms, auto-unlink cascade confirms.
	| 'link_drift'
	// Auto-sunset (2026-09-20): record a `board_action_sunset` activity for
	// every Adverse Action item whose board actions have all ended, so What
	// Workd Did counts real events instead of deriving a figure from end dates.
	// Runs before stats_refresh so the tile sees this run's events.
	| 'board_action_sunset'
	| 'stats_refresh';

/**
 * Durable per-stage run history for the nightly licensing pipeline
 * (PD-14439/PD-14440). One row per (runKey, stage), written by the
 * crm-api stage-runner as each stage starts and finishes.
 *
 * Lives in the TENANT database on purpose: it is auto-created by the
 * database-sync (additive), writable by the tenant app user with existing
 * grants, and the config-api central stack reads it cross-tenant (with its
 * privileged credential) as the "did everyone finish last night?" doneness
 * check before harvesting. Replaces "console logs + ephemeral BullMQ job
 * progress" as the app-side record of what ran.
 *
 * auditable: false — this IS an ops log; mirroring every insert/update into
 * `_audit` would only duplicate it (same reasoning as
 * license_compliance_snapshot).
 */
@Index('idx_lpr_run_key', ['runKey'])
@Index('idx_lpr_started_at', ['startedAt'])
@Entity('license_pipeline_run', {}, { auditable: false })
export class LicensePipelineRunEntity {
	@PrimaryGeneratedColumn({ name: '_id', unsigned: true })
	public id!: number;

	/**
	 * Groups the stage rows of one pipeline run — the BullMQ job id for
	 * queue-driven runs, or a `cli-…` key for matchmaker-cli runs.
	 */
	@Column('varchar', { name: 'runKey', length: 191 })
	public runKey!: string;

	/** Which pipeline produced the run: FULL_SCAN | INCREMENTAL_SCAN. */
	@Column('varchar', { name: 'jobType', length: 64 })
	public jobType!: string;

	@Column('varchar', { name: 'stage', length: 64 })
	public stage!: LicensePipelineStage;

	@Column('varchar', { name: 'status', length: 32, default: 'running' })
	public status!: LicensePipelineStageStatus;

	/**
	 * UTC, and compared against the database clock in SQL — both by
	 * `TIMESTAMPDIFF(…, startedAt, NOW())` when reporting how long a run has
	 * taken and by the config-api harvester's `startedAt >= DATE_SUB(NOW(),
	 * INTERVAL 26 HOUR)` doneness check.
	 *
	 * The writer sends a JS `Date`, and mysql2 serialises that from the
	 * *process's* local wall clock, so the value is only comparable with `NOW()`
	 * while the process runs in UTC. Every entry point pins itself, and the
	 * DataSource factory enforces it as a backstop — see
	 * `apps/crm-api/src/shared/utils/process-timezone.ts` (PD-14998).
	 */
	@Column('datetime', {
		name: 'startedAt',
		default: () => 'CURRENT_TIMESTAMP',
	})
	public startedAt!: Date;

	/** UTC, under the same invariant as `startedAt`. */
	@Column('datetime', { name: 'completedAt', nullable: true })
	public completedAt!: Date | null;

	/**
	 * Stage-specific counters, e.g. match `{processed, matched}`, prune
	 * `{scanned, pruneCandidates, pruned, byReason, abortedReason?}`,
	 * auto_confirm `{candidates, confirmed, dryRun}`.
	 */
	@Column('json', { name: 'counts', nullable: true })
	public counts!: Record<string, unknown> | null;

	@Column('text', { name: 'error', nullable: true })
	public error!: string | null;
}
